import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { cn } from 'lib/utils'
import useMediaQuery from 'hooks/useMediaQuery'
import { useChatPopup } from 'features/Chat/ChatPopupContext'
import { useChat } from 'features/Chat/ChatContext'
import ChatPopupHeaderBar from './ChatPopupHeaderBar'
import ConversationList from './ConversationList'
import ThreadView from './ThreadView'
import ComposeView from './ComposeView'
import type { IChatHeader } from 'features/Chat/Chat.types'
import { centredIn, clampToViewport, type Point } from 'components/ui/floatingGeometry'

/**
 * The global chat, as a popup available from every screen.
 *
 * Not built on components/ui/dialog: Dialog hard-locks body scroll and owns its
 * child's position, whereas on desktop this is a free-floating, draggable panel
 * that leaves the app behind it usable. It portals directly, at a z-index above
 * dialog (10002) and sheet (10001), so chat opened over a modal still wins.
 *
 * Desktop position is held as explicit pixel left/top rather than centering
 * classes, so dragging and the open animation cannot fight over `transform`.
 */

const ANIMATION_MS = 250

const PANEL_W = 380
const PANEL_MAX_H = 640

// The keep-on-screen geometry is shared with the floating panel (MYD-644).
// Re-exported, so this module's API and its tests are unchanged.
export { clampToViewport }
export type { Point }

export const panelHeight = (viewportH = window.innerHeight) =>
	Math.min(PANEL_MAX_H, viewportH - 96)

/**
 * Where the panel sits when it opens: dead centre, both axes.
 *
 * Exported with explicit viewport arguments so the geometry is testable
 * without a layout engine.
 */
export const centredPosition = (
	viewportW = window.innerWidth,
	viewportH = window.innerHeight,
): Point => centredIn(PANEL_W, panelHeight(viewportH), viewportW, viewportH)

const ChatPopup = () => {
	const { isOpen, view, pendingEventId, room, close, showList, showThread, showCompose, clearPendingEventId } =
		useChatPopup()
	const {
		state: { currentChatHeader, isSocketConnected },
		actions: { openThread, handleStartNewChat },
	} = useChat()

	const isDesktop = useMediaQuery('(min-width: 768px)')
	const panelRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<Element | null>(null)
	// Kept mounted through the exit transition so the panel animates out.
	const [isMounted, setIsMounted] = useState(false)
	const [isVisible, setIsVisible] = useState(false)

	// Desktop panel position. Null until the panel is measured on open, at
	// which point it is centred. Dragging replaces it.
	const [position, setPosition] = useState<Point | null>(null)
	const [isDragging, setIsDragging] = useState(false)
	// Grab offset within the header, so the panel does not jump to put its
	// corner under the cursor on the first move.
	const dragOffset = useRef<{ dx: number; dy: number; width: number; height: number } | null>(null)

	useEffect(() => {
		if (isOpen) {
			triggerRef.current = document.activeElement
			setIsMounted(true)
			// Next frame, so the enter transition has a start state to run from.
			const id = requestAnimationFrame(() => setIsVisible(true))
			return () => cancelAnimationFrame(id)
		}

		setIsVisible(false)
		const id = setTimeout(() => setIsMounted(false), ANIMATION_MS)
		return () => clearTimeout(id)
	}, [isOpen])

	// Return focus to whatever opened the popup.
	useEffect(() => {
		if (isOpen || !triggerRef.current) return
		;(triggerRef.current as HTMLElement).focus?.()
		triggerRef.current = null
	}, [isOpen])

	// Jump straight to a thread when opened with one, e.g. from a Home card.
	useEffect(() => {
		if (!isOpen || !pendingEventId) return
		openThread(pendingEventId)
		clearPendingEventId()
	}, [isOpen, pendingEventId, openThread, clearPendingEventId])

	// Escape steps back one level before closing, which is what a two-level
	// popup is expected to do.
	useEffect(() => {
		if (!isOpen) return
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			e.stopPropagation()
			if (view === 'list') close()
			else showList()
		}
		document.addEventListener('keydown', onKeyDown)
		return () => document.removeEventListener('keydown', onKeyDown)
	}, [isOpen, view, close, showList])

	/**
	 * Click-away dismissal.
	 *
	 * The panel is a floating window, not a modal, so a click on the app behind
	 * it should put it away rather than be swallowed.
	 *
	 * Three things are deliberately NOT "outside":
	 *   - the header trigger, which owns its own toggle; without this exclusion
	 *     the two fire together and the panel reopens on the same click;
	 *   - anything Radix has portalled (the emoji and media pickers), which is
	 *     visually inside the popup but a sibling of it in the DOM;
	 *   - a confirm modal (`data-popup-layer`), for the same reason.
	 *
	 * pointerdown rather than click, so a drag that ends outside the panel does
	 * not register as a click-away, and so the panel closes on press like every
	 * other popover in the app.
	 */
	useEffect(() => {
		if (!isOpen) return

		const onPointerDown = (e: PointerEvent) => {
			const target = e.target as HTMLElement | null
			if (!target || !panelRef.current) return
			if (panelRef.current.contains(target)) return
			if (typeof target.closest !== 'function') return
			if (
				target.closest('[data-testid="chat-popup-trigger"]') ||
				target.closest('[data-radix-popper-content-wrapper]') ||
				target.closest('[data-popup-layer]')
			) {
				return
			}
			close()
		}

		document.addEventListener('pointerdown', onPointerDown)
		return () => document.removeEventListener('pointerdown', onPointerDown)
	}, [isOpen, close])

	// Centre on every open. Layout effect so the panel is painted in place
	// rather than flashing at the top-left for a frame. Cleared on close so the
	// next open centres again rather than reusing a stale drag position.
	useLayoutEffect(() => {
		if (!isMounted || !isDesktop) {
			setPosition(null)
			return
		}
		setPosition(centredPosition())
	}, [isMounted, isDesktop])

	// A window resize can leave a dragged panel partly or wholly off screen.
	useEffect(() => {
		if (!isMounted || !isDesktop) return
		const onResize = () => {
			setPosition(prev =>
				prev ? clampToViewport(prev, PANEL_W, panelHeight()) : centredPosition(),
			)
		}
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [isMounted, isDesktop])

	const handleDragStart = useCallback(
		(e: React.PointerEvent) => {
			// Mobile is full-screen: there is nowhere to drag it to.
			if (!isDesktop) return
			// Let the back, compose and close buttons be buttons.
			if ((e.target as HTMLElement).closest('button')) return

			const rect = panelRef.current?.getBoundingClientRect()
			if (!rect) return

			dragOffset.current = {
				dx: e.clientX - rect.left,
				dy: e.clientY - rect.top,
				width: rect.width,
				height: rect.height,
			}
			setIsDragging(true)
			// Capture on the header, so a fast drag that outruns the cursor
			// still delivers move and up events here.
			;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
		},
		[isDesktop],
	)

	const handleDragMove = useCallback((e: React.PointerEvent) => {
		const offset = dragOffset.current
		if (!offset) return
		e.preventDefault()
		setPosition(
			clampToViewport(
				{ x: e.clientX - offset.dx, y: e.clientY - offset.dy },
				offset.width,
				offset.height,
			),
		)
	}, [])

	const handleDragEnd = useCallback((e: React.PointerEvent) => {
		if (!dragOffset.current) return
		dragOffset.current = null
		setIsDragging(false)
		;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
	}, [])

	if (!isMounted) return null

	const handleSelect = (chatHeader: IChatHeader) => {
		openThread(chatHeader._id, chatHeader.receiverIds)
		showThread()
	}

	const handleCompose = () => {
		handleStartNewChat()
		showCompose()
	}

	const title =
		view === 'thread'
			? // A room has no DM header, so it carries its own name.
				room?.label || currentChatHeader?.label || 'Conversation'
			: view === 'compose'
				? 'New Message'
				: 'Messages'

	const panel = (
		<div
			ref={panelRef}
			data-testid="chat-popup"
			role={isDesktop ? 'region' : 'dialog'}
			aria-modal={isDesktop ? undefined : true}
			aria-label="Messages"
			className={cn(
				'fixed z-[10003] flex flex-col overflow-hidden bg-white ease-out',
				// Position is animated only on open. Transitioning left/top would
				// make the panel lag a frame behind the cursor while dragging.
				isDragging ? 'transition-none' : 'transition-[opacity,transform] duration-[250ms]',
				isDesktop
					? [
							'h-[min(640px,calc(100vh-6rem))] w-[380px] rounded-2xl border border-gray-200 shadow-2xl',
							isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
							// Hidden until measured, so it cannot flash at 0,0.
							position ? 'visible' : 'invisible',
						]
					: ['inset-0', isVisible ? 'translate-y-0' : 'translate-y-full'],
			)}
			style={
				isDesktop && position ? { left: `${position.x}px`, top: `${position.y}px` } : undefined
			}
		>
			<ChatPopupHeaderBar
				title={title}
				subtitle={isSocketConnected ? undefined : 'Reconnecting…'}
				onBack={view === 'list' ? undefined : showList}
				onCompose={view === 'list' ? handleCompose : undefined}
				onClose={close}
				isDraggable={isDesktop}
				isDragging={isDragging}
				onPointerDown={handleDragStart}
				onPointerMove={handleDragMove}
				onPointerUp={handleDragEnd}
				onPointerCancel={handleDragEnd}
			/>

			{view === 'list' && <ConversationList onSelect={handleSelect} />}
			{view === 'thread' && <ThreadView isActive={isOpen && view === 'thread'} />}
			{view === 'compose' && <ComposeView isActive={isOpen && view === 'compose'} />}
		</div>
	)

	return ReactDOM.createPortal(panel, document.body)
}

export default ChatPopup
